import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { 
  Star,
  Loader2,
  User,
  Package,
  Plane,
  Truck,
  MessageSquare,
  Clock
} from "lucide-react";

export default function ReviewUsers() {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchPendingReviews = useCallback(async () => {
    try {
      const response = await api.get("/reviews/pending");
      setPendingReviews(response.data.pending_reviews || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Failed to fetch pending reviews:", error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingReviews();
  }, [fetchPendingReviews]);

  const handleOpenReview = (service) => {
    setSelectedService(service);
    setRating(0);
    setHoverRating(0);
    setReviewText("");
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/reviews", {
        reviewee_id: selectedService.user_id,
        service_type: selectedService.service_type,
        service_id: selectedService.service_id,
        rating: rating,
        review_text: reviewText || null
      });
      
      toast.success("Review submitted successfully!");
      setShowReviewModal(false);
      fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case "local_delivery":
        return <Package className="w-5 h-5" />;
      case "cargo_booking":
        return <Truck className="w-5 h-5" />;
      case "jet_booking":
        return <Plane className="w-5 h-5" />;
      default:
        return <Package className="w-5 h-5" />;
    }
  };

  const getServiceLabel = (type) => {
    switch (type) {
      case "local_delivery":
        return "Local Delivery";
      case "cargo_booking":
        return "Cargo Booking";
      case "jet_booking":
        return "Jet Booking";
      default:
        return "Service";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-black tracking-tight mb-2">Review Users</h1>
        <p className="text-muted-foreground">Rate users after completed services</p>
      </div>

      {pendingReviews.length === 0 ? (
        <div className="text-center py-16 border border-border">
          <Star className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground mb-2">No pending reviews</p>
          <p className="text-sm text-muted-foreground">Complete more deliveries to review users</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{pendingReviews.length} service(s) waiting for review</p>
          
          {pendingReviews.map((service) => (
            <div key={service.service_id} className="border border-border p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                  {getServiceIcon(service.service_type)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-bold">{service.user_name}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{getServiceLabel(service.service_type)}</p>
                  <p className="text-xs text-muted-foreground mt-1">{service.details}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="text-right text-sm text-muted-foreground">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {service.completed_at ? new Date(service.completed_at).toLocaleDateString() : "N/A"}
                </div>
                <Button 
                  onClick={() => handleOpenReview(service)}
                  className="rounded-none"
                  data-testid={`review-btn-${service.service_id}`}
                >
                  <Star className="w-4 h-4 mr-2" />
                  Review
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Review {selectedService?.user_name}
            </DialogTitle>
            <DialogDescription>
              {selectedService && getServiceLabel(selectedService.service_type)} - Rate your experience
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Star Rating */}
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm text-muted-foreground">Tap to rate</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`w-10 h-10 transition-colors ${
                        star <= (hoverRating || rating)
                          ? "fill-amber-400 text-amber-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-sm font-medium">
                  {rating === 1 && "Poor"}
                  {rating === 2 && "Fair"}
                  {rating === 3 && "Good"}
                  {rating === 4 && "Very Good"}
                  {rating === 5 && "Excellent"}
                </p>
              )}
            </div>

            {/* Written Review */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Written Review (Optional)
              </label>
              <Textarea
                placeholder="Share your experience with this user..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="rounded-none resize-none"
                rows={3}
                maxLength={500}
                data-testid="review-text"
              />
              <p className="text-xs text-muted-foreground text-right">{reviewText.length}/500</p>
            </div>

            <Button
              className="w-full rounded-none"
              onClick={handleSubmitReview}
              disabled={submitting || rating === 0}
              data-testid="submit-review-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
